import React from 'react';
import { ResponsiveImageSet as KeyVisual } from '@core/ui';
import KeyVisualXs from '@core/assets/images/keyvisual-xs-rgb.webp';
import KeyVisualLg from '@core/assets/images/keyvisual-lg-rgb.webp';
import PageNotFoundHeader from './PageNotFoundHeader';
import HeroTitle from './HeroTitle';
import HeroLeadGraphic from './HeroLeadGraphic';
import HeroCTA from './HeroCTA';
import { useHeroConfig, useKeyVisualConfig, useStandardKeyVisualConfig } from '../../stores/index.js';
import { getCardStyle } from './heroUtils';
import styles from './HeroBackground.module.scss';

const HeroBackground = ({ pageNotFound, maxOverlayHeight, variant = 'hero' }) => {
    const heroConfig = useHeroConfig();
    const {
        isFullWidth: isKeyVisualFullWidth,
        breakpoints: keyVisualBreakpoints,
        altText: keyVisualAltText,
    } = useKeyVisualConfig();
    const {
        isFullWidth: isStandardKeyVisualFullWidth,
        breakpoints: standardKeyVisualBreakpoints,
        altText: standardKeyVisualAltText,
    } = useStandardKeyVisualConfig();

    const isSplit =
        heroConfig.background === 'keyVisual' &&
        (heroConfig.keyVisualSplit === 'split-left' || heroConfig.keyVisualSplit === 'split-right');

    const useStandardHero = variant === 'standard';

    // The standard key visual has its own full-width flag; on the 404 page the regular
    // key visual container applies even in standard mode.
    const containerIsFullWidth = useStandardHero && !pageNotFound ? isStandardKeyVisualFullWidth : isKeyVisualFullWidth;

    return (
        <div className={containerIsFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>
            {pageNotFound ? (
                <PageNotFoundHeader />
            ) : useStandardHero ? (
                <KeyVisual
                    imageXs={KeyVisualXs}
                    imageLg={KeyVisualLg}
                    breakpoints={standardKeyVisualBreakpoints}
                    altText={standardKeyVisualAltText}
                />
            ) : heroConfig.background === 'solid' ? (
                <div
                    className={`w-100 ${styles.solidBackground} ${isKeyVisualFullWidth ? styles.fullWidth : ''}`}
                    style={{
                        backgroundColor: heroConfig.solidColor || undefined,
                        minHeight: maxOverlayHeight > 0 ? `${maxOverlayHeight}px` : undefined,
                    }}
                />
            ) : (
                <div
                    className={`${styles.heroBackgroundContainer} ${
                        heroConfig.keyVisualSplit === 'split-left' ? styles.splitLeft : ''
                    } ${heroConfig.keyVisualSplit === 'split-right' ? styles.splitRight : ''} ${
                        isKeyVisualFullWidth ? styles.fullWidth : ''
                    }`}
                    style={{
                        backgroundColor: heroConfig.solidColor || 'transparent',
                        minHeight: maxOverlayHeight > 0 ? `${maxOverlayHeight}px` : undefined,
                    }}
                >
                    {(heroConfig.keyVisualSplit === 'split-left' || heroConfig.keyVisualSplit === 'split-right') && (
                        <div className={styles.naturalHeightDummy} aria-hidden="true">
                            <KeyVisual
                                imageXs={KeyVisualXs}
                                imageLg={KeyVisualLg}
                                breakpoints={keyVisualBreakpoints}
                                altText=""
                            />
                        </div>
                    )}
                    <div className={styles.keyVisualWrapper}>
                        <KeyVisual
                            imageXs={KeyVisualXs}
                            imageLg={KeyVisualLg}
                            breakpoints={keyVisualBreakpoints}
                            altText={keyVisualAltText}
                        />
                    </div>
                    {isSplit && (
                        <div className={`d-block d-md-none ${styles.mobileSplitContent}`}>
                            {heroConfig.title?.hasTitle && (
                                <div className={styles.pointerEventsNone}>
                                    <HeroTitle titleConfig={heroConfig.title} />
                                </div>
                            )}
                            {heroConfig.leadGraphic?.hasLeadGraphic && (
                                <div className={styles.pointerEventsNone}>
                                    <HeroLeadGraphic leadGraphicConfig={heroConfig.leadGraphic} />
                                </div>
                            )}
                            {heroConfig.cta?.hasCTA && (
                                <div
                                    className={
                                        heroConfig.cta?.card?.hasNoCard
                                            ? styles.ctaNoCard
                                            : `card text-start ${styles.ctaCard}`
                                    }
                                    style={
                                        heroConfig.cta?.card?.hasNoCard
                                            ? { pointerEvents: 'auto' }
                                            : {
                                                  pointerEvents: 'auto',
                                                  ...getCardStyle(heroConfig.cta),
                                              }
                                    }
                                >
                                    <HeroCTA ctaConfig={heroConfig.cta} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HeroBackground;
